// src/helpers/editorHelpers.js
import { loadDocContent } from './docHelpers';
import { apiLoadDocMeta, computeDocFingerprint } from '../api/docs';
import { createContentSignature } from './versionHelpers';
import { debug } from './debug';
import { isEditorContentEmpty } from './contentGuards';
import { updateTabLabelLocal as updateTabLabel } from './tabHelpers';

const crossPaneDocStateCache = new Map();
const loadDocForEditorInFlight = new Map();

export const cacheDocState = (docId, state) => {
  if (!docId) return;
  const previous = crossPaneDocStateCache.get(String(docId)) || {};
  crossPaneDocStateCache.set(String(docId), {
    content: state?.content ?? null,
    layout: state?.layout ?? null,
    name: state?.name ?? '',
    updated_at: state?.updated_at ?? null,
    has_edits: state?.hasEdits ?? state?.has_edits ?? previous?.has_edits ?? false,
    db_fingerprint: state?.dbFingerprint ?? state?.db_fingerprint ?? previous?.db_fingerprint ?? null,
    cached_at: Date.now()
  });
};

export const getCachedDocState = (docId) => {
  if (!docId) return null;
  return crossPaneDocStateCache.get(String(docId)) || null;
};

export const normalizeEditorContent = (value) => {
  if (!value || typeof value !== 'object') return null;
  if (
    Object.prototype.hasOwnProperty.call(value, 'json') &&
    Object.prototype.hasOwnProperty.call(value, 'html')
  ) {
    return value;
  }
  if (value.type === 'doc') {
    return {
      json: value,
      html: ''
    };
  }
  return null;
};

export const loadDocForEditor = async (docId) => {
  const key = String(docId);
  const inFlight = loadDocForEditorInFlight.get(key);
  if (inFlight) {
    return inFlight;
  }

  const request = (async () => {
  const cached = getCachedDocState(docId);
  const cachePayload = cached
    ? {
        docId,
        content: cached.content,
        layout: cached.layout ?? null,
        name: cached.name ?? '',
        updated_at: cached.updated_at ?? null,
        db_fingerprint: cached.db_fingerprint ?? null,
        has_edits: Boolean(cached.has_edits)
      }
    : null;

  const cacheHasContent = cachePayload ? !isEditorContentEmpty(cachePayload.content) : false;
  const cacheTs = cachePayload?.updated_at ? new Date(cachePayload.updated_at).getTime() : 0;
  const cacheIsUnsaved = Boolean(cachePayload?.has_edits);

  if (cachePayload && cacheHasContent && !cacheIsUnsaved) {
    try {
      const meta = await apiLoadDocMeta(docId);
      const dbMetaTs = meta?.updated_at ? new Date(meta.updated_at).getTime() : 0;
      const dbMetaFingerprint = meta?.fingerprint || null;
      const cacheDbFingerprint = cachePayload?.db_fingerprint || null;
      const fingerprintMatch = Boolean(dbMetaFingerprint) && Boolean(cacheDbFingerprint) && dbMetaFingerprint === cacheDbFingerprint;
      const canUseTsFallback = !dbMetaFingerprint || !cacheDbFingerprint;
      const tsFallbackMatch = canUseTsFallback && dbMetaTs > 0 && cacheTs >= dbMetaTs;

      if (fingerprintMatch || tsFallbackMatch) {
        debug('loadDocForEditor', { type: 'log' }, `source=cache reason=meta-up-to-date docId=${docId} fpMatch=${fingerprintMatch} tsFallback=${tsFallbackMatch}`);
        return {
          docId,
          content: cachePayload.content || '',
          layout: cachePayload.layout ?? meta.layout ?? null,
          name: cachePayload.name || meta.name || '',
          updated_at: cachePayload.updated_at || meta.updated_at || null,
          db_fingerprint: dbMetaFingerprint || cacheDbFingerprint || null
        };
      }
    } catch (err) {
      debug('loadDocForEditor', { type: 'warn' }, `meta check failed; fallback=db docId=${docId} error=${err?.message || err}`);
    }
  }

  const db = await loadDocContent(docId);
  if (db?.__loadFailed) {
    debug('loadDocForEditor', { type: 'warn' }, `source=db reason=load-failed docId=${docId} code=${db?.__errorCode || 'DOC_LOAD_FAILED'}`);
    return {
      docId,
      __loadFailed: true,
      __errorCode: db?.__errorCode || 'DOC_LOAD_FAILED',
      __errorMessage: db?.__errorMessage || 'Unable to load document content'
    };
  }

  const dbPayload = {
    docId,
    content: db?.content || '',
    layout: db?.layout || null,
    name: db?.name || '',
    updated_at: db?.updated_at || null
  };

  const dbFingerprint = computeDocFingerprint(db?.content_json || null);

  const dbHasContent = !isEditorContentEmpty(dbPayload.content);

  const dbTs = dbPayload.updated_at ? new Date(dbPayload.updated_at).getTime() : 0;
  const cacheIsNewer = cacheTs > dbTs;

  let selected = dbPayload;
  let reason = 'db-default';
  if (cacheHasContent) {
    if (!dbHasContent) {
      selected = cachePayload;
      reason = 'cache-db-empty';
    } else if (cacheIsUnsaved || cacheIsNewer) {
      selected = cachePayload;
      reason = cacheIsUnsaved ? 'cache-unsaved-newer' : 'cache-newer-than-db';
    }
  }

  const selectedFromCache = selected === cachePayload;
  if (!selectedFromCache) {
    const dbSignature = createContentSignature(dbPayload.content);
    const cacheSignature = cachePayload ? createContentSignature(cachePayload.content) : null;
    const signaturesDiffer = dbSignature && dbSignature !== cacheSignature;
    const shouldRefreshCache = !cachePayload || signaturesDiffer || cacheTs < dbTs;
    if (shouldRefreshCache) {
      cacheDocState(docId, {
        content: dbPayload.content,
        layout: dbPayload.layout,
        name: dbPayload.name,
        updated_at: dbPayload.updated_at,
        dbFingerprint,
        hasEdits: false
      });
    }
    reason = 'db-selected';
  } else if (reason === 'db-default') {
    reason = 'cache-selected';
  }

  debug('loadDocForEditor', { type: 'log' }, `source=${selectedFromCache ? 'cache' : 'db'} reason=${reason} docId=${docId} cacheHasContent=${cacheHasContent} cacheUnsaved=${cacheIsUnsaved} dbHasContent=${dbHasContent}`);

  return {
    docId,
    content: selected.content || '',
    layout: selected.layout || null,
    name: selected.name || '',
    updated_at: selected.updated_at || null,
    db_fingerprint: selected.db_fingerprint || dbFingerprint || null
  };
  })();

  loadDocForEditorInFlight.set(key, request);
  try {
    return await request;
  } finally {
    if (loadDocForEditorInFlight.get(key) === request) {
      loadDocForEditorInFlight.delete(key);
    }
  }
};

// Show HTML content in the editor (for HTML view mode)
export function showHTML(editor, setContent) {
  if (!editor || !setContent) return;
  setContent(editor.getHTML());
  debug('[Toolbar] HTML content:', editor.getHTML());
}

// Re-export pure updateTabLabel from tabHelpers for backward compatibility
export { updateTabLabel };

// Focus editor on a specific doc (stub for possible future use)
export const focusEditorOnDoc = (editorRef, docId) => {
  if (!editorRef?.current) return;
  debug(`[editorHelpers] Focus editor on doc ${docId}`);
};

// Mark a doc as saved in editor state (stub for possible future use)
export const markDocSaved = (editorStates, docId) => {
  if (!editorStates[docId]) return editorStates;
  return {
    ...editorStates,
    [docId]: { ...editorStates[docId], hasEdits: false }
  };
};

// Normalize editor content (e.g., strip trailing spaces)
export const normalizeContent = (content = '') => content.trim();

// Update the editor title in state
export const updateEditorTitle = (editorStates, docId, newTitle) => {
  if (!editorStates[docId]) return editorStates;
  return {
    ...editorStates,
    [docId]: { ...editorStates[docId], title: newTitle }
  };
};

// Validate editor JSON (accepts either raw editor JSON or wrapper { json, html })
export function isValidEditorJson(payload) {
  const toValidate = payload && payload.json ? payload.json : payload;
  if (!toValidate || typeof toValidate !== 'object') return false;
  // Top-level doc type
  if (toValidate.type !== 'doc') return false;
  const validateNode = (node) => {
    if (!node || typeof node !== 'object') return false;
    // Text nodes may appear with a `text` property and no `type` in some serializations
    if (typeof node.text === 'string') return true;
    if (typeof node.type !== 'string') return false;
    if (node.content && Array.isArray(node.content)) {
      return node.content.every(n => validateNode(n));
    }
    return true;
  };

  if (!Array.isArray(toValidate.content)) return false;
  return toValidate.content.every(n => validateNode(n));
}

// Attach capture-phase drag/drop handlers to an editor DOM node.
export function attachEditorDomHandlers(editor, handlersRef, key, handleEditorDrop) {
  try {
    try { console.debug('[editorHelpers] attachEditorDomHandlers', { key, hasView: !!editor, hasDom: !!(editor && editor.view && editor.view.dom) }); } catch (e) {}
    if (!editor || !editor.view || !editor.view.dom) return;
    const dom = editor.view.dom;
    if (!handlersRef || !handlersRef.current) return;
    if (handlersRef.current[key]) return; // already attached

    const dropHandler = (ev) => {
      try { ev.preventDefault(); ev.stopPropagation(); } catch (e) {}
      try { handleEditorDrop(ev); } catch (e) {}
    };
    const dragoverHandler = (ev) => { try { ev.preventDefault(); } catch (e) {} };

      dom.addEventListener('drop', dropHandler, true);
      dom.addEventListener('dragover', dragoverHandler, true);
      handlersRef.current[key] = { dom, dropHandler, dragoverHandler };
  } catch (err) {
    // swallow errors - defensive
  }
}

export function detachEditorDomHandlers(handlersRef, key) {
  try {
    if (!handlersRef || !handlersRef.current) return;
    const handlers = handlersRef.current[key];
    if (!handlers) return;
    try { handlers.dom.removeEventListener('drop', handlers.dropHandler, true); } catch (e) {}
    try { handlers.dom.removeEventListener('dragover', handlers.dragoverHandler, true); } catch (e) {}
    delete handlersRef.current[key];
  } catch (err) {}
}
